import { Component } from 'react';
import { reportError } from '../lib/errorReporting';

// Top-level safety net: catches any render/lifecycle error anywhere in the tree below it
// (a malformed chat message, an unexpected API response shape, a null contact field, etc.)
// and shows a friendly recovery screen instead of an unrecoverable blank white page.
//
// React error boundaries MUST be class components - there is no hook equivalent for
// getDerivedStateFromError / componentDidCatch as of this React version.
//
// Recovery is safe by design: almost all RepBuddy state (chats, credits, seller profile) is
// persisted server-side in Supabase, so a reload re-fetches clean state rather than re-
// triggering the same crash - this is not just a "sorry" screen, it's a real fix path.
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Console stays: it is what a developer looks at first, and it works even when error
    // reporting is switched off (locally, or before a DSN is configured).
    console.error('[ErrorBoundary] Unhandled render error:', error, info?.componentStack);
    // Also report it, so a crash a customer hits at 3am is not lost with their browser tab.
    // No-op unless REACT_APP_SENTRY_DSN is set.
    reportError(error, { boundary: 'root' });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div
        style={{
          minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexDirection: 'column', gap: '18px', background: 'var(--bg-page, #050505)',
          fontFamily: 'var(--font-display, monospace)', color: 'var(--text-primary, #eaf4ee)',
          textAlign: 'center', padding: '24px',
        }}
      >
        <div style={{ fontSize: '15px', fontWeight: 600 }}>
          Something went wrong on this page.
        </div>
        <div style={{ fontSize: '13px', color: 'var(--text-secondary, #93a79c)', maxWidth: '420px', lineHeight: 1.6 }}>
          Your chats and account data are safe - they're saved on our servers, not in this
          browser tab. Reloading almost always fixes this.
        </div>
        <div style={{ display: 'flex', gap: '10px', marginTop: '6px' }}>
          <button
            onClick={this.handleReload}
            style={{
              fontFamily: 'inherit', fontSize: '13px', fontWeight: 600, padding: '9px 18px',
              background: 'var(--grad-accent, #17a94e)', border: 'none', color: '#06170c',
              borderRadius: '100px', cursor: 'pointer',
            }}
          >
            Reload page
          </button>
          <button
            onClick={this.handleGoHome}
            style={{
              fontFamily: 'inherit', fontSize: '13px', fontWeight: 600, padding: '9px 18px',
              background: 'transparent', border: '1px solid var(--border-bright, rgba(124,255,107,0.3))',
              color: 'var(--text-primary, #eaf4ee)', borderRadius: '100px', cursor: 'pointer',
            }}
          >
            Go to home page
          </button>
        </div>
      </div>
    );
  }
}
